import { createAsyncThunk } from "@reduxjs/toolkit"

import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { rejectWithHandledError } from "../../../utils/handlers/reject-with-handled-error.ts"
import { LEADS_SAVE_RESULT } from "../../Leads/constants/leads-save-results.ts"
import { readProjectManagerRequired } from "../../Leads/helpers/project-manager-required.ts"
import { leadsSetProp } from "../../Leads/redux/leads.slice.ts"
import * as services from "../../StatusAutomation/api/status-automation.services.ts"
import { readStatusRefusal } from "../../StatusAutomation/helpers/status-automation.ts"
import type {
  LeadSuggestionAnswer,
  LeadUndoAnswer,
  StatusSuggestion,
  StatusSuggestionAcceptBody
} from "../../StatusAutomation/types/status-automation.types.ts"

interface SuggestionArgs {
  leadId: string
  suggestionId: string
}

interface RefusalBody {
  type?: string
  project_managers?: unknown[]
  message?: string
  missing?: string[]
  fields?: string[]
  pending_commitments?: unknown[]
  pending_signatures?: unknown[]
}

// A refusal may carry its fields at the top level, under `details`, or split
// between them (`type` on top, `project_managers` under `details`), so the
// two are read together with `details` winning.
const refusalBody = (data: unknown): RefusalBody | undefined => {
  const body = data as (RefusalBody & { details?: RefusalBody }) | undefined
  if (!body || typeof body !== "object") return undefined
  const details =
    body.details && typeof body.details === "object" ? body.details : {}
  const merged = { ...body, ...details }
  delete (merged as { details?: unknown }).details
  return merged.type ? merged : undefined
}

export const acceptLeadStatusSuggestion = createAsyncThunk<
  LeadSuggestionAnswer,
  SuggestionArgs & { target: number | null; body?: StatusSuggestionAcceptBody }
>(
  "lead/acceptStatusSuggestion",
  async (
    { leadId, suggestionId, target, body },
    { dispatch, rejectWithValue }
  ) => {
    const response = await services.acceptStatusSuggestion(
      "lead",
      leadId,
      suggestionId,
      body
    )

    if (!response.ok) {
      const refusal = readStatusRefusal(response.data)
      if (response.status === 409 && refusal) return rejectWithValue(refusal)

      const details = refusalBody(response.data)
      // Answered with the PM prompt, not a toast — as the status change is.
      if (readProjectManagerRequired(details)) return rejectWithValue(details)
      // The archive dialog that asked turns this into its diary question.
      if (details?.type === LEADS_SAVE_RESULT.PENDING_COMMITMENTS) {
        return rejectWithValue({
          type: LEADS_SAVE_RESULT.PENDING_COMMITMENTS,
          message: details.message,
          pending_commitments: details.pending_commitments ?? [],
          pending_signatures: details.pending_signatures ?? []
        })
      }
      // The same answer a manual move gets: marks on the cards and Update Now.
      if (details?.type === LEADS_SAVE_RESULT.MISSING_FIELDS) {
        dispatch(
          leadsSetProp({
            key: "saveResult",
            value: {
              type: LEADS_SAVE_RESULT.MISSING_FIELDS,
              message: details.message,
              missing: details.missing,
              fields: details.fields,
              targetStatus: target ?? undefined,
              leadId
            }
          })
        )
        return rejectWithValue({ type: LEADS_SAVE_RESULT.MISSING_FIELDS })
      }
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "lead.acceptStatusSuggestion"
      })
    }

    return unwrapEnvelope<LeadSuggestionAnswer>(response.data)
  }
)

export const dismissLeadStatusSuggestion = createAsyncThunk<
  { status_suggestions: StatusSuggestion[] },
  SuggestionArgs
>(
  "lead/dismissStatusSuggestion",
  async ({ leadId, suggestionId }, { dispatch, rejectWithValue }) => {
    const response = await services.dismissStatusSuggestion(
      "lead",
      leadId,
      suggestionId
    )

    if (!response.ok) {
      const refusal = readStatusRefusal(response.data)
      if (response.status === 409 && refusal) return rejectWithValue(refusal)
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "lead.dismissStatusSuggestion"
      })
    }

    return unwrapEnvelope<{ status_suggestions: StatusSuggestion[] }>(
      response.data
    )
  }
)

export const undoLeadStatusTransition = createAsyncThunk<
  LeadUndoAnswer,
  { leadId: string; transitionId: string }
>(
  "lead/undoStatusTransition",
  async ({ leadId, transitionId }, { dispatch, rejectWithValue }) => {
    const response = await services.undoStatusTransition(
      "lead",
      leadId,
      transitionId
    )

    if (!response.ok) {
      const refusal = readStatusRefusal(response.data)
      if (response.status === 409 && refusal) return rejectWithValue(refusal)
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "lead.undoStatusTransition"
      })
    }

    return unwrapEnvelope<LeadUndoAnswer>(response.data)
  }
)
